import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import Utils from "Utils/utils";
import userDetail from "Stores/userDetail";
import LiveFeedUtils from "Pages/Dashboard/dashboard/LiveFeed/utils";
import { months } from "Pages/Dashboard/dashboard/LiveFeed/constants";
import { ReactComponent as LinkedinIcon } from "Assets/svg/linkedInOutlined.svg";
import inboxV2Store from "./inboxV2Store";

// Relative timestamp identical to LinkedInReplyContentPopup (Today / Yesterday / date).
const formatLiDate = value => {
  if (!value) return "";
  const date = new Date(value);
  const mon = months[date.getMonth()];
  const hours = ((date.getHours() + 11) % 12) + 1;
  const mins = date.getMinutes();
  const meridiem = date.getHours() >= 12 ? "PM" : "AM";
  const hh = hours === 12 && meridiem === "AM" ? "00" : hours;
  const mm = mins > 9 ? mins : `0${mins}`;
  if (LiveFeedUtils.isYesterday(date)) return `Yesterday - ${hh}:${mm} ${meridiem}`;
  if (LiveFeedUtils.isToday(date)) return `Today - ${hh}:${mm} ${meridiem}`;
  return `${mon} ${date.getDate()}, ${date.getFullYear()} - ${hh}:${mm} ${meridiem}`;
};

const OwnerChip = ({ ownerName = "", ownerProfileUrl = "", profilePictureUrl = "" }) => {
  const [broken, setBroken] = useState(false);
  return (
  <span className="inboxV2LiSender">
    <span className="inboxV2LiSenderPic">
      {profilePictureUrl && !broken ? (
        <img src={profilePictureUrl} alt="" onError={() => setBroken(true)} />
      ) : (
        <span className="material-icons">account_circle</span>
      )}
    </span>
    <a href={ownerProfileUrl} target="_blank" rel="noopener noreferrer">
      {ownerName}
    </a>
  </span>
  );
};

const LiAvatar = ({ imageUrl = "" }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="inboxV2LiBubbleAvatar">
      {imageUrl && !broken ? (
        <img src={imageUrl} alt="" onError={() => setBroken(true)} />
      ) : (
        <span className="material-icons">account_circle</span>
      )}
    </span>
  );
};

const LinkedInThread = observer((props = {}) => {
  const { conversation = {} } = props || {};
  const [text, setText] = useState("");
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const chatRef = useRef(null);

  const {
    messages = [],
    canViewLiReplies = true,
    ownerName = "",
    ownerProfileUrl = "",
    profilePictureUrl = "",
    liConnected = true
  } = inboxV2Store?.liReply || {};
  const loading = inboxV2Store?.liReplyLoading;
  const sending = inboxV2Store?.liSending;

  const contact = inboxV2Store?.selectedContact || {};
  const contactId = inboxV2Store.getContactId(contact);
  const isOldLinkedInAccount = contact?.isOldLinkedInAccount || false;

  useEffect(() => {
    if (contactId) {
      inboxV2Store.getLinkedInReply(contactId, contact?.linkedinAccountId || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, conversation?.id]);

  // Owner = the conversation's LinkedIn account is the current user's own (mirrors the
  // backend's owner test). Owner -> plain send; admin -> "replying as {owner}".
  const isOwner = (userDetail?.activeLinkedInAccounts || []).some(
    account =>
      account?.accountId && account.accountId === contact?.linkedinAccountId
  );
  const canReply = !isOldLinkedInAccount && canViewLiReplies && liConnected;
  const blockReason = !canViewLiReplies
    ? "You don’t have permission to reply to this conversation. Only the owner of the connected LinkedIn account can reply."
    : isOldLinkedInAccount
      ? "Replies aren’t available for this LinkedIn account."
      : !liConnected
        ? isOwner
          ? "Your LinkedIn account is disconnected. Reconnect to continue messaging."
          : `${ownerName}’s LinkedIn account is disconnected. Ask ${ownerName} to reconnect.`
        : "";

  // liReply.messages are newest-first; a chat reads oldest-first (newest at the bottom).
  const orderedMessages = [...messages].reverse();

  const scrollToBottom = () => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Button click glides down; the on-load jump stays instant.
  const jumpToLatest = () => {
    const el = chatRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // Land at the bottom (latest message) whenever the conversation loads or grows.
  useEffect(() => {
    scrollToBottom();
  }, [messages.length, loading]);

  const onScroll = () => {
    const el = chatRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
    setShowScrollBtn(!atBottom);
  };

  const send = async () => {
    if (!canReply) return;
    const ok = await inboxV2Store.sendLinkedInReply(text);
    if (ok) setText("");
  };

  return (
    <div className="inboxV2ThreadPane inboxV2LiPane">
      <div className="inboxV2LiCard">
      <div className="inboxV2LiHead">
        <span className="inboxV2LiHeadIcon">
          <LinkedinIcon />
        </span>
        <div className="inboxV2LiHeadMeta">
          <div className="inboxV2LiHeadTitle">LinkedIn conversation</div>
        </div>
        <button
          type="button"
          className="inboxV2Close"
          onClick={() => inboxV2Store.clearSelection()}
        >
          <i className="material-icons">close</i>
        </button>
      </div>

      <div className="inboxV2LiChat" ref={chatRef} onScroll={onScroll}>
        {loading ? (
          <div className="inboxV2EditorLoading inboxV2LiLoading">
            <span className="inboxV2Spinner" />
            <span>Loading conversation...</span>
          </div>
        ) : !canViewLiReplies ? (
          <div className="inboxV2LiPermission">
            You don’t have permission to view this LinkedIn conversation. Only the
            owner of the connected LinkedIn account can access replies.
          </div>
        ) : (
          <>
            {liConnected && !isOwner && ownerName && (
              <div className="inboxV2LiInfo inboxV2LiInfoCenter">
                You're viewing a LinkedIn conversation received on:{" "}
                <OwnerChip
                  ownerName={ownerName}
                  ownerProfileUrl={ownerProfileUrl}
                  profilePictureUrl={profilePictureUrl}
                />
                .
              </div>
            )}
            {conversation?.step && (
              <div className="inboxV2LiStepRow">
                <span className="inboxV2Pill">{conversation.step}</span>
              </div>
            )}
            {orderedMessages.map((item, index) => {
              const {
                senderName = "",
                updateTs = "",
                imageUrl = "",
                message = "",
                date = ""
              } = item || {};
              const msgDate = date || updateTs;
              const isOut =
                !!ownerName &&
                (senderName || "").trim().toLowerCase() ===
                  ownerName.trim().toLowerCase();
              return (
                <div
                  className={`inboxV2LiMessage ${isOut ? "out" : "in"}`}
                  key={`liMsg${index}`}
                >
                  <LiAvatar imageUrl={isOut ? profilePictureUrl : imageUrl} />
                  <div className="inboxV2LiBubbleWrap">
                    <div
                      className="inboxV2LiBubble"
                      dangerouslySetInnerHTML={{
                        __html: Utils.contentReadyOnly(message)
                      }}
                    />
                    <div className="inboxV2LiDate">
                      {formatLiDate(msgDate)}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Chat composer, always visible. */}
      <div className="inboxV2LiComposer">
        {canReply && !isOwner && ownerName && (
          <div className="inboxV2LiComposerNote">
            You're replying as{" "}
            <OwnerChip
              ownerName={ownerName}
              ownerProfileUrl={ownerProfileUrl}
              profilePictureUrl={profilePictureUrl}
            />
          </div>
        )}
        {!canReply && blockReason && (
          <div className="inboxV2LiBlockReason">{blockReason}</div>
        )}
        <div className="inboxV2LiInputBox">
          <textarea
            value={text}
            rows={2}
            placeholder={
              canReply ? "Write a message..." : "You can’t reply to this conversation"
            }
            disabled={!canReply || sending}
            onChange={event => setText(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <div className="inboxV2LiComposerFooter">
            <span className="inboxV2LiComposerHint">
              Press Enter to send · Shift + Enter for a new line
            </span>
            <button
              type="button"
              className="inboxV2LiSendBtn"
              disabled={!canReply || sending || !text.trim()}
              onClick={send}
            >
              {sending ? "Sending..." : "Send"}
              <i className="material-icons">send</i>
            </button>
          </div>
        </div>
      </div>
      </div>

      {showScrollBtn && (
        <button
          type="button"
          className="inboxV2LiScrollDown"
          onClick={jumpToLatest}
        >
          <i className="material-icons">keyboard_arrow_down</i>
          Latest messages
        </button>
      )}
    </div>
  );
});

export default LinkedInThread;
